/**
 * Public Self-service Onboarding Portal
 * ======================================
 * No-auth page mounted at /onboarding/:token. Validates the token, renders a
 * branded KYC form, submits to /api/onboarding/public/:token/submit, then shows
 * a confirmation screen with the reference id + (if applicable) a soft notice
 * that compliance review is in progress.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, Loader2, AlertTriangle, CheckCircle2,
  User, Mail, Phone, MapPin, Calendar, FileText, Building2,
} from "lucide-react";

const PALETTE = {
  bg: "#080c12", card: "#0d1117", border: "#1e2530", text: "#f1f5f9",
  muted: "#94a3b8", subtle: "#64748b", accent: "#2563eb", error: "#ef4444",
  success: "#22c55e", warn: "#f59e0b",
};

const ID_TYPES = ["PAN", "AADHAAR", "PASSPORT", "DL", "VOTER_ID"];
const NATIONALITIES = [
  { code: "IN", label: "India" }, { code: "US", label: "United States" },
  { code: "GB", label: "United Kingdom" }, { code: "AE", label: "UAE" },
  { code: "SG", label: "Singapore" }, { code: "CA", label: "Canada" },
  { code: "AU", label: "Australia" }, { code: "PK", label: "Pakistan" },
  { code: "OTHER", label: "Other" },
];

const inputStyle = { width: "100%", background: "#080c12", border: `1px solid ${PALETTE.border}`, borderRadius: 8, padding: "12px 14px", color: PALETTE.text, fontSize: 14, outline: "none", transition: "border-color 0.15s" };
const labelStyle = { color: PALETTE.muted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6, display: "block" };

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label style={labelStyle}>
        {Icon && <Icon size={11} style={{ display: "inline", marginRight: 4, verticalAlign: "middle" }} />}
        {label}
      </label>
      {children}
    </div>
  );
}

function Logo() {
  return (
    <a href="https://rudrik.io" target="_blank" rel="noopener noreferrer" style={{ display: "inline-block" }}>
      <img src="/assets/rudrik-logo-full.png" alt="Rudrik" style={{ height: 36, filter: "drop-shadow(0 2px 8px rgba(37, 99, 235, 0.4))" }} />
    </a>
  );
}

export default function PublicOnboardingPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    full_name: "", date_of_birth: "", gender: "", nationality: "IN",
    occupation: "", email: "", phone: "", address: "",
    id_type: "PAN", id_number: "", pan: "",
    company_name: "", cin: "", registered_address: "",
    notes: "",
  });

  const isCorporate = meta?.customer_type === "corporate";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`/api/onboarding/public/${token}`);
        setMeta(data);
        if (data.prefill?.name) setForm((f) => ({ ...f, full_name: data.prefill.name }));
        if (data.prefill?.email) setForm((f) => ({ ...f, email: data.prefill.email }));
      } catch (e) {
        setError(e.response?.data?.detail || "Invalid or expired link");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    if (isCorporate && !form.company_name.trim()) { alert("Company name is required."); return; }
    if (!isCorporate && !form.full_name.trim()) { alert("Full name is required."); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`/api/onboarding/public/${token}/submit`, form);
      setResult(data);
    } catch (e) {
      alert(e.response?.data?.detail || "Submission failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  // ---------- Loading ----------
  if (loading) return (
    <PortalShell><div style={{ textAlign: "center", padding: "60px 20px", color: PALETTE.muted }}>
      <Loader2 className="animate-spin" style={{ width: 36, height: 36, margin: "0 auto 14px", color: PALETTE.accent }} />
      <div>Verifying secure link…</div>
    </div></PortalShell>
  );

  // ---------- Error ----------
  if (error) return (
    <PortalShell><div style={{ textAlign: "center", padding: "60px 20px" }} data-testid="onboarding-error">
      <AlertTriangle style={{ width: 48, height: 48, color: PALETTE.error, margin: "0 auto 18px" }} />
      <h2 style={{ color: PALETTE.text, fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Link Unavailable</h2>
      <p style={{ color: PALETTE.muted, fontSize: 14, maxWidth: 420, margin: "0 auto" }}>{error}</p>
      <p style={{ color: PALETTE.subtle, fontSize: 12, marginTop: 20 }}>Please contact your compliance representative for a new link.</p>
    </div></PortalShell>
  );

  // ---------- Success ----------
  if (result) return (
    <PortalShell><div style={{ textAlign: "center", padding: "50px 20px" }} data-testid="onboarding-success">
      <div style={{ width: 72, height: 72, borderRadius: "50%", background: `${PALETTE.success}22`, border: `2px solid ${PALETTE.success}55`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 22px" }}>
        <CheckCircle2 style={{ width: 38, height: 38, color: PALETTE.success }} />
      </div>
      <h2 style={{ color: PALETTE.text, fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Submission Received</h2>
      <p style={{ color: PALETTE.muted, fontSize: 14, maxWidth: 480, margin: "0 auto 22px" }}>
        Thank you. Your application has been securely submitted to {meta.branding.company_name}'s compliance team.
        You will be contacted within 1–2 business days.
      </p>
      <div style={{ background: PALETTE.bg, border: `1px solid ${PALETTE.border}`, borderRadius: 8, padding: "14px 18px", display: "inline-block", textAlign: "left" }}>
        <div style={{ color: PALETTE.subtle, fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>Reference ID</div>
        <code style={{ color: PALETTE.accent, fontSize: 13, fontFamily: "ui-monospace, monospace" }} data-testid="onboarding-ref-id">{result.reference_id}</code>
      </div>
      {result.watchlist_match && (
        <div style={{ marginTop: 22, padding: 14, background: `${PALETTE.warn}11`, border: `1px solid ${PALETTE.warn}55`, borderRadius: 8, color: PALETTE.warn, fontSize: 12, maxWidth: 480, margin: "22px auto 0" }}>
          ⓘ Additional verification is in progress. Our team may reach out for supporting documents.
        </div>
      )}
    </div></PortalShell>
  );

  // ---------- Form ----------
  return (
    <PortalShell>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ color: PALETTE.text, fontSize: 24, fontWeight: 700, margin: 0, marginBottom: 6 }} data-testid="onboarding-title">
          {isCorporate ? "Corporate KYC Application" : "Customer KYC Application"}
        </h1>
        <p style={{ color: PALETTE.muted, fontSize: 13 }}>
          {meta.branding.company_name} uses Rudrik to verify your identity in compliance with PMLA / RBI / SEBI norms.
          All data is encrypted in transit and at rest.
        </p>
      </div>

      <form onSubmit={submit}>
        {/* DigiLocker quick-verify */}
        {!isCorporate && (
          <div data-testid="ob-digilocker-cta" style={{ marginBottom: 18, padding: 14, background: "linear-gradient(135deg, rgba(37,99,235,0.08), rgba(30,64,175,0.04))", border: "1px solid #2563eb55", borderRadius: 10, display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ background: "rgba(37,99,235,0.15)", width: 42, height: 42, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <ShieldCheck size={20} style={{ color: "#3b82f6" }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9" }}>Verify instantly via DigiLocker</div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>Skip paperwork. Pull your gov-verified Aadhaar + PAN in 30 seconds.</div>
            </div>
            <button
              type="button"
              data-testid="ob-digilocker-btn"
              onClick={async () => {
                try {
                  const { data } = await axios.post(`/api/digilocker/authorize`, {
                    onboarding_token: token,
                    origin_url: window.location.origin,
                  });
                  if (data.authorize_url) window.location.href = data.authorize_url;
                } catch (e) {
                  alert(e.response?.data?.detail || "DigiLocker is unavailable. Please fill the form manually.");
                }
              }}
              style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)", color: "#fff", border: "none", padding: "10px 18px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}
            >
              <ShieldCheck size={13} /> Use DigiLocker
            </button>
          </div>
        )}

        {/* Personal / Corporate identity */}
        <Section title={isCorporate ? "Company Details" : "Personal Details"}>
          {isCorporate ? (
            <>
              <Field label="Legal Company Name *" icon={Building2}>
                <input data-testid="ob-company-name" required type="text" value={form.company_name} onChange={(e) => change("company_name", e.target.value)} style={inputStyle} placeholder="e.g. Acme Industries Pvt Ltd" />
              </Field>
              <Field label="CIN" icon={FileText}>
                <input data-testid="ob-cin" type="text" value={form.cin} onChange={(e) => change("cin", e.target.value.toUpperCase())} style={inputStyle} maxLength={21} placeholder="L12345MH2000PLC123456" />
              </Field>
              <Field label="Registered Address" icon={MapPin}>
                <textarea value={form.registered_address} onChange={(e) => change("registered_address", e.target.value)} style={{ ...inputStyle, minHeight: 70, resize: "vertical" }} placeholder="Street, City, State, PIN" />
              </Field>
              <Field label="Authorised Signatory Name *" icon={User}>
                <input data-testid="ob-full-name" required type="text" value={form.full_name} onChange={(e) => change("full_name", e.target.value)} style={inputStyle} placeholder="Director / Authorised person" />
              </Field>
            </>
          ) : (
            <>
              <Field label="Full Name (as per ID) *" icon={User}>
                <input data-testid="ob-full-name" required type="text" value={form.full_name} onChange={(e) => change("full_name", e.target.value)} style={inputStyle} placeholder="e.g. Rohit Sharma" />
              </Field>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Field label="Date of Birth" icon={Calendar}>
                  <input type="date" value={form.date_of_birth} onChange={(e) => change("date_of_birth", e.target.value)} style={inputStyle} />
                </Field>
                <Field label="Gender">
                  <select value={form.gender} onChange={(e) => change("gender", e.target.value)} style={inputStyle}>
                    <option value="">Select…</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="O">Other / Prefer not to say</option>
                  </select>
                </Field>
              </div>
            </>
          )}
        </Section>

        {/* Contact + Nationality */}
        <Section title="Contact & Nationality">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Field label="Email" icon={Mail}>
              <input type="email" value={form.email} onChange={(e) => change("email", e.target.value)} style={inputStyle} placeholder="you@example.com" />
            </Field>
            <Field label="Phone" icon={Phone}>
              <input type="tel" value={form.phone} onChange={(e) => change("phone", e.target.value)} style={inputStyle} placeholder="+91 98xxx xxxxx" />
            </Field>
          </div>
          <Field label={isCorporate ? "Country of Incorporation" : "Nationality"}>
            <select value={form.nationality} onChange={(e) => change("nationality", e.target.value)} style={inputStyle} data-testid="ob-nationality">
              {NATIONALITIES.map((n) => <option key={n.code} value={n.code}>{n.label}</option>)}
            </select>
          </Field>
          {!isCorporate && (
            <Field label="Occupation">
              <input type="text" value={form.occupation} onChange={(e) => change("occupation", e.target.value)} style={inputStyle} placeholder="e.g. Software Engineer, Business Owner" />
            </Field>
          )}
          <Field label="Address" icon={MapPin}>
            <textarea value={form.address} onChange={(e) => change("address", e.target.value)} style={{ ...inputStyle, minHeight: 70, resize: "vertical" }} placeholder="Street, City, State, PIN" />
          </Field>
        </Section>

        {/* ID + PAN */}
        <Section title="Identity Verification">
          <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 14 }}>
            <Field label="ID Type">
              <select value={form.id_type} onChange={(e) => change("id_type", e.target.value)} style={inputStyle} data-testid="ob-id-type">
                {ID_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="ID Number">
              <input type="text" value={form.id_number} onChange={(e) => change("id_number", e.target.value.toUpperCase())} style={inputStyle} placeholder="As per selected ID" data-testid="ob-id-number" />
            </Field>
          </div>
          <Field label="PAN (Required by RBI for INR transactions ≥ ₹50k)">
            <input type="text" value={form.pan} onChange={(e) => change("pan", e.target.value.toUpperCase())} style={inputStyle} maxLength={10} placeholder="ABCDE1234F" data-testid="ob-pan" />
          </Field>
        </Section>

        {/* Notes */}
        <Section title="Additional Notes (Optional)">
          <textarea value={form.notes} onChange={(e) => change("notes", e.target.value)} style={{ ...inputStyle, minHeight: 80, resize: "vertical" }} placeholder="Source of funds, business purpose, or anything you'd like our compliance team to know." />
        </Section>

        {/* Consent + Submit */}
        <div style={{ background: PALETTE.bg, border: `1px solid ${PALETTE.border}`, borderRadius: 10, padding: 16, fontSize: 12, color: PALETTE.muted, lineHeight: 1.6, marginBottom: 20 }}>
          By submitting, I confirm that the information provided is true and complete to the best of my knowledge. I authorise
          {" "}{meta.branding.company_name}{" "}and Rudrik to verify it against sanctions / PEP / adverse-media / watchlist
          databases in accordance with PMLA 2002 and applicable RBI / SEBI / FIU-IND guidelines.
        </div>

        <button data-testid="ob-submit-btn" type="submit" disabled={submitting}
          style={{
            width: "100%", background: `linear-gradient(135deg, ${PALETTE.accent}, #1d4ed8)`,
            border: "none", borderRadius: 10, color: "white", padding: "14px",
            fontWeight: 700, fontSize: 14, cursor: submitting ? "wait" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            boxShadow: "0 4px 18px rgba(37, 99, 235, 0.35)",
          }}>
          {submitting ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
          {submitting ? "Submitting securely…" : "Submit Application"}
        </button>
      </form>
    </PortalShell>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background: PALETTE.card, border: `1px solid ${PALETTE.border}`, borderRadius: 12, padding: 22, marginBottom: 18 }}>
      <h3 style={{ color: PALETTE.text, fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, margin: "0 0 16px", paddingBottom: 10, borderBottom: `1px solid ${PALETTE.border}` }}>{title}</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{children}</div>
    </div>
  );
}

function PortalShell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: PALETTE.bg, color: PALETTE.text, padding: "32px 16px 60px" }} data-testid="onboarding-page">
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
          <Logo />
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: PALETTE.subtle, fontSize: 11 }}>
            <ShieldCheck size={12} style={{ color: PALETTE.success }} />
            Secured by Rudrik AML
          </div>
        </div>
        {children}
        <div style={{ marginTop: 32, textAlign: "center", color: PALETTE.subtle, fontSize: 10 }}>
          © 2026 Rudrik Technologies Private Limited · <a href="https://rudrik.io" target="_blank" rel="noopener noreferrer" style={{ color: PALETTE.subtle }}>rudrik.io</a>
        </div>
      </div>
    </div>
  );
}
